import React, { useMemo, useState } from 'react';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import Toast from '../../components/support/Toast';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import { InputAdornment, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DataProviderInstance } from '../../api/DataProvider';
import {
  DAYS_PER_MONTH,
  ageInDays,
  formatPercentile,
  formatZ,
  measurementStats,
  parseDate,
  validateMeasurement,
} from '../../pages/appointment/modules/growth/growthIndicators';
import styles from './GrowthMeasurementPopup.module.css';

const DATE_FORMAT = 'YYYY-MM-DD';

const FIELDS = [
  { key: 'weight', indicator: 'weight', label: 'Peso', unit: 'kg', placeholder: '0,000' },
  { key: 'height', indicator: 'height', label: 'Comprimento/altura', unit: 'cm', placeholder: '0,0' },
  { key: 'headCircumference', indicator: 'head', label: 'Perímetro cefálico', unit: 'cm', placeholder: '0,0' },
];

const toInput = (value) => (value == null ? '' : String(value).replace('.', ','));
const toPayload = (value) => (value.trim() === '' ? '' : value.trim().replace(',', '.'));

/** Data padrão: a do atendimento (nunca no futuro), senão hoje. */
const defaultDate = (appointmentDate) => {
  const today = dayjs().format(DATE_FORMAT);
  if (!appointmentDate || !dayjs(appointmentDate).isValid()) return today;
  const date = dayjs(appointmentDate).format(DATE_FORMAT);
  return date > today ? today : date;
};

/** Posição padrão da OMS para a idade: deitado até 24 meses, em pé depois. */
const defaultPosition = (birthday, date) => {
  const days = ageInDays(birthday, date);
  return days != null && days / DAYS_PER_MONTH >= 24 ? 'standing' : 'lying';
};

/**
 * Registro ou edição de uma medição da curva de crescimento: data, peso,
 * comprimento/altura (com a posição da medida) e perímetro cefálico. Mostra
 * a idade na data e o percentil de cada valor enquanto se digita.
 */
function GrowthMeasurementPopup({ patientId, appointmentId, appointmentDate, entry, birthday, gender, OnHide }) {
  const { SetPatientGrowthMeasurement, DeletePatientGrowthMeasurement } = DataProviderInstance();

  const [date, setDate] = useState(entry ? dayjs(entry.measuredAt).format(DATE_FORMAT) : defaultDate(appointmentDate));
  const [values, setValues] = useState({
    weight: toInput(entry?.weight),
    height: toInput(entry?.height),
    headCircumference: toInput(entry?.headCircumference),
  });
  const [position, setPosition] = useState(entry?.heightPosition || defaultPosition(birthday, date));
  const [saving, setSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

  const showToast = (message, severity = 'error') => setToast({ open: true, message, severity });

  const payload = {
    id: entry?.id,
    measuredAt: date,
    weight: toPayload(values.weight),
    height: toPayload(values.height),
    headCircumference: toPayload(values.headCircumference),
    heightPosition: toPayload(values.height) === '' ? '' : position,
  };

  const preview = useMemo(
    () => measurementStats(payload, birthday, gender),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date, values, position, birthday, gender]
  );

  function handleDateChange(value) {
    const next = value && value.isValid() ? value.format(DATE_FORMAT) : null;
    setDate(next);
    // Sem posição escolhida à mão, acompanha a idade da nova data
    if (next && !entry?.heightPosition) setPosition(defaultPosition(birthday, next));
  }

  function saveHandler() {
    if (saving) return;
    const error = validateMeasurement(payload, birthday);
    if (error) {
      showToast(error);
      return;
    }

    setSaving(true);
    SetPatientGrowthMeasurement(patientId, entry ? entry.appointmentId : appointmentId, payload, (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast(response?.error || 'Não foi possível salvar a medição.');
      }
    });
  }

  function deleteHandler() {
    DeletePatientGrowthMeasurement(entry.id, (response) => {
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast('Não foi possível excluir a medição.');
      }
    });
  }

  function renderHint(field) {
    const stats = preview.indicators[field.indicator];
    if (!stats) return <span className={styles.hint} />;
    if (stats.status === 'missing') return <span className={styles.hint}>Sem percentil: falta nascimento ou sexo no cadastro</span>;
    if (stats.status === 'out_of_range') return <span className={styles.hint}>Idade fora da curva da OMS</span>;
    return (
      <span className={`${styles.hint} ${stats.implausible ? styles.hintAlert : ''}`}>
        {stats.implausible
          ? `Muito fora da curva (z ${formatZ(stats.z)}). Confira o valor.`
          : `P ${formatPercentile(stats.percentile)} · z ${formatZ(stats.z)}`}
      </span>
    );
  }

  const birth = parseDate(birthday);
  const bmiStats = preview.indicators.bmi;

  const content = (
    <div className={styles.container}>
      <div className={styles.header}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Data da medição"
            value={date ? dayjs(date) : null}
            format="DD/MM/YYYY"
            disableFuture={true}
            minDate={birth != null ? dayjs(birthday) : undefined}
            onChange={handleDateChange}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
        {preview.days != null && preview.days >= 0 && (
          <span className={styles.age}>Idade na medição: <strong>{preview.ageLabel}</strong></span>
        )}
      </div>

      <div className={styles.rows}>
        {FIELDS.map((field) => (
          <div key={field.key} className={styles.row}>
            <span className={styles.rowLabel}>{field.label}</span>
            <TextField
              size="small"
              className={styles.field}
              value={values[field.key]}
              placeholder={field.placeholder}
              onChange={(e) => setValues({ ...values, [field.key]: e.target.value.replace(/[^0-9.,]/g, '') })}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{ endAdornment: <InputAdornment position="end">{field.unit}</InputAdornment> }}
            />
            {renderHint(field)}
          </div>
        ))}

        {values.height.trim() !== '' && (
          <div className={styles.row}>
            <span className={styles.rowLabel}>Como foi medido</span>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={position}
              onChange={(e, value) => value && setPosition(value)}
            >
              <ToggleButton value="lying" className={styles.toggle}>Deitado</ToggleButton>
              <ToggleButton value="standing" className={styles.toggle}>Em pé</ToggleButton>
            </ToggleButtonGroup>
            <span className={styles.hint}>A OMS usa deitado até 2 anos; na outra posição a curva ajusta 0,7 cm</span>
          </div>
        )}

        {bmiStats && (
          <div className={styles.row}>
            <span className={styles.rowLabel}>IMC calculado</span>
            <span className={styles.bmi}>{bmiStats.value.toFixed(1).replace('.', ',')} kg/m²</span>
            {renderHint({ indicator: 'bmi' })}
          </div>
        )}
      </div>

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      {entry && (
        <CustomButton
          variant="contained"
          style="error"
          label="Excluir"
          icon={<IconDelete />}
          onClick={() => setShowConfirmDelete(true)}
          enabled={!saving}
        />
      )}

      <CustomButton
        variant="outlined"
        style="secondary"
        label="Cancelar"
        onClick={() => OnHide(false)}
        enabled={!saving}
      />

      <CustomButton
        style="primary"
        variant="contained"
        label={entry ? 'Atualizar' : 'Registrar'}
        icon={<IconSave />}
        onClick={saveHandler}
        loading={saving}
        enabled={!saving}
      />

      {showConfirmDelete && (
        <ConfirmPopup
          Visibility={showConfirmDelete}
          Title="Confirmar exclusão"
          Description="Tem certeza que deseja excluir esta medição?"
          ConfirmLabel="Excluir"
          OnConfirm={deleteHandler}
          OnHide={() => setShowConfirmDelete(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title={entry ? 'Editar medição' : 'Nova medição'}
      Content={content}
      Footer={footer}
      OnClose={() => OnHide(false)}
      maxWidth="sm"
    />
  );
}

export default GrowthMeasurementPopup;
